import { createContext, useCallback, useContext, useState } from 'react';

/**
 * Simulates the runtime session state of flow nodes (WaitForAll gateways)
 * without any backend. The config panel exposes "Mark as done" buttons per branch.
 *
 * In production, this state lives in Drupal's EcaState KeyValue store,
 * isolated per session_id. Here it lives in React state per nodeId.
 */

const ExecutionMockContext = createContext(null);

export const useExecutionMock = () => {
  const ctx = useContext(ExecutionMockContext);
  if (!ctx) throw new Error('useExecutionMock must be used inside ExecutionMockProvider');
  return ctx;
};

/**
 * Session state shape (per gateway nodeId):
 * {
 *   sessionId: string,
 *   branches: { [branchId]: 'pending' | 'done' },
 *   fired: boolean,
 *   reset_after_fire: boolean,
 * }
 */
export const ExecutionMockProvider = ({ children }) => {
  const [sessions, setSessions] = useState({});

  /** Initialize or reset a gateway's session state */
  const initGateway = useCallback((nodeId, branchIds, resetAfterFire) => {
    setSessions((prev) => ({
      ...prev,
      [nodeId]: {
        sessionId: `session_${Date.now()}`,
        branches: Object.fromEntries(branchIds.map((id) => [id, 'pending'])),
        fired: false,
        reset_after_fire: resetAfterFire,
      },
    }));
  }, []);

  /** Mark one branch as done; fires continuation if all done */
  const markBranchDone = useCallback((nodeId, branchId) => {
    setSessions((prev) => {
      const session = prev[nodeId];
      if (!session || session.fired) return prev;

      const updatedBranches = { ...session.branches, [branchId]: 'done' };
      const allDone = Object.values(updatedBranches).every((s) => s === 'done');

      if (allDone) {
        if (session.reset_after_fire) {
          // Repeatable: reset all branches to pending after firing
          return {
            ...prev,
            [nodeId]: {
              ...session,
              branches: Object.fromEntries(
                Object.keys(session.branches).map((k) => [k, 'pending'])
              ),
              fired: false,
            },
          };
        }
        // One-shot: mark as fired, keep state
        return { ...prev, [nodeId]: { ...session, branches: updatedBranches, fired: true } };
      }

      return { ...prev, [nodeId]: { ...session, branches: updatedBranches } };
    });
  }, []);

  /** Manually reset a gateway's session (for demo purposes) */
  const resetGateway = useCallback((nodeId) => {
    setSessions((prev) => {
      const session = prev[nodeId];
      if (!session) return prev;
      return {
        ...prev,
        [nodeId]: {
          ...session,
          branches: Object.fromEntries(
            Object.keys(session.branches).map((k) => [k, 'pending'])
          ),
          fired: false,
        },
      };
    });
  }, []);

  /** Update the reset_after_fire setting for a gateway */
  const setResetAfterFire = useCallback((nodeId, value) => {
    setSessions((prev) => {
      const session = prev[nodeId];
      if (!session) return prev;
      return { ...prev, [nodeId]: { ...session, reset_after_fire: value } };
    });
  }, []);

  const getSession = useCallback((nodeId) => sessions[nodeId] ?? null, [sessions]);

  return (
    <ExecutionMockContext.Provider
      value={{ initGateway, markBranchDone, resetGateway, setResetAfterFire, getSession }}
    >
      {children}
    </ExecutionMockContext.Provider>
  );
};
